/**
 * Context for managing and sharing roadmap-related state in the application in the client screen.
 * Provides various states like nodes, edges, progress, visibility, and zoom level.
 */

import { createContext, useState } from 'react';

/**
 * @typedef {Object} ClientRoadmapContextType
 * @property {Array} nodes - List of nodes in the roadmap.
 * @property {Function} setNodes - Function to update the nodes state.
 * @property {Array} edges - List of edges connecting the nodes.
 * @property {Function} setEdges - Function to update the edges state.
 * @property {Object} learningProgress - Object containing the user's learning progress.
 * @property {Function} setLearningProgress - Function to update the learning progress state.
 * @property {Number} contentCompleted - The number of completed content pieces in the roadmap.
 * @property {Function} setContentCompleted - Function to update the completed content state.
 * @property {String} roadmapTitle - The title of the roadmap.
 * @property {Function} setRoadmapTitle - Function to update the roadmap title.
 * @property {Boolean} isBookmarked - Whether the roadmap is bookmarked by the user.
 * @property {Function} setIsBookmarked - Function to update the bookmarked state.
 * @property {Number} totalRoadmapContent - The total number of content pieces in the roadmap.
 * @property {Function} setTotalRoadmapContent - Function to update the total content state.
 * @property {Object|null} premiumAccessData - Premium access information for the roadmap.
 * @property {Function} setPremiumAccessData - Function to update the premium access data.
 * @property {Number} roadmapPrice - The price of the roadmap.
 * @property {Function} setRoadmapPrice - Function to update the roadmap price.
 * @property {Object|null} selectedNode - The currently selected node.
 * @property {Function} setSelectedNode - Function to update the selected node.
 * @property {Number} zoomLevel - The zoom level for the roadmap.
 * @property {Function} setZoomLevel - Function to update the zoom level.
 * @property {Boolean} isRoadmapFetching - Whether the roadmap data is being fetched.
 * @property {Function} setIsRoadmapFetching - Function to update the roadmap fetching state.
 */

/** @type {React.Context<ClientRoadmapContextType>} */
export const ClientRoadmapContext = createContext();

/**
 * Context provider that wraps the application or specific components
 * and provides access to the roadmap context value.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components that will have access to the context state.
 * @returns {JSX.Element} The Context Provider component.
 */
export const ClientRoadmapContextProvider = ({ children }) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [learningProgress, setLearningProgress] = useState({});
  const [contentCompleted, setContentCompleted] = useState(0);
  const [roadmapTitle, setRoadmapTitle] = useState('');
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [totalRoadmapContent, setTotalRoadmapContent] = useState(0);
  const [grossRoadmapContent, setGrossRoadmapContent] = useState(0);
  const [premiumAccessData, setPremiumAccessData] = useState(null);
  const [roadmapPrice, setRoadmapPrice] = useState(0.0);
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [isRoadmapFetching, setIsRoadmapFetching] = useState(false);

  return (
    <ClientRoadmapContext.Provider
      value={{
        nodes,
        setNodes,
        edges,
        setEdges,
        learningProgress,
        setLearningProgress,
        contentCompleted,
        setContentCompleted,
        roadmapTitle,
        setRoadmapTitle,
        isBookmarked,
        setIsBookmarked,
        totalRoadmapContent,
        setTotalRoadmapContent,
        grossRoadmapContent,
        setGrossRoadmapContent,
        premiumAccessData,
        setPremiumAccessData,
        roadmapPrice,
        setRoadmapPrice,
        selectedNode,
        setSelectedNode,
        zoomLevel,
        setZoomLevel,
        isRoadmapFetching,
        setIsRoadmapFetching,
      }}
    >
      {children}
    </ClientRoadmapContext.Provider>
  );
};
